import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import contactHttpClient from "utils/contactHttpClient";
import { toast } from "react-toastify";
import Acquirer from "enums/Acquirer";

export function useStripePaymentIntent(contactToken: string | null = null) {
  const requestVersion = useRef(0);
  const location = useLocation();

  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [customerSessionClientSecret, setCustomerSessionClientSecret] =
    useState<string | null>(null);
  const [acquirer, setAcquirer] = useState<Acquirer | null>(null);
  const [publishableKey, setPublishableKey] = useState<string | null>(null);
  const [componentType, setComponentType] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [allowedPaymentMethods, setAllowedPaymentMethods] = useState<string[]>(
    [],
  );
  const [paymentMethodType, setPaymentMethodType] = useState("");

  const isCompleteRoute = location.pathname.includes("/pay-invoice/complete");

  useEffect(() => {
    requestVersion.current += 1;
    if (!isCompleteRoute) {
      setClientSecret(null);
      setCustomerSessionClientSecret(null);
      setLoading(false);
    }
    return () => {
      requestVersion.current += 1;
    };
  }, [contactToken, isCompleteRoute]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlClientSecret = params.get("payment_intent_client_secret");
    if (isCompleteRoute && urlClientSecret) {
      setClientSecret(urlClientSecret);
    }
  }, [location, isCompleteRoute]);

  const startCheckout = async ({
    invoiceId,
    recipientId,
  }: {
    invoiceId: string;
    recipientId: string;
  }) => {
    if (isCompleteRoute) return; // Prevent starting checkout if already on the complete route

    const version = ++requestVersion.current;
    setLoading(true);

    try {
      const res = await contactHttpClient.post("/payments/intent", {
        invoiceId,
        recipientId,
      });
      if (version !== requestVersion.current) return;
      const data = res.data;
      setAllowedPaymentMethods(data.allowedPaymentMethods || []);
      setPaymentMethodType(data.paymentMethodType || "");
      setClientSecret(data.clientSecret);
      setCustomerSessionClientSecret(data.customerSessionClientSecret);
      setAcquirer(data.acquirer ?? null);
      setPublishableKey(data.publishableKey ?? null);
      setComponentType(data.componentType ?? null);
    } catch (err) {
      if (version !== requestVersion.current) return;
      const code =
        responseErrorOf(err) || messageOf(err) || "Error starting checkout";

      toast.error(code);
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  };

  const changePaymentMethod = async ({
    paymentMethodType: nextMethod,
    invoiceId,
    recipientId,
  }: {
    paymentMethodType: string;
    invoiceId: string;
    recipientId: string;
  }) => {
    const paymentIntentId = clientSecret?.split("_secret")[0];
    if (!paymentIntentId || loading) return;
    setLoading(true);
    try {
      await contactHttpClient.patch(`/payments/intent/${paymentIntentId}`, {
        paymentMethodType: nextMethod,
        invoiceId,
        recipientId,
      });
      setPaymentMethodType(nextMethod);
    } catch (error) {
      toast.error(responseErrorOf(error) || "Could not change payment method");
    } finally {
      setLoading(false);
    }
  };

  return {
    allowedPaymentMethods,
    paymentMethodType,
    changePaymentMethod,
    clientSecret,
    customerSessionClientSecret,
    acquirer,
    publishableKey,
    componentType,
    loading,
    startCheckout,
  };
}

const responseErrorOf = (error: unknown): string | undefined =>
  (error as { response?: { data?: { error?: string } } })?.response?.data
    ?.error;

const messageOf = (error: unknown): string | undefined =>
  (error as { message?: string })?.message;
